import type { KeyboardEvent } from 'react'
import { useEffect, useState } from 'react'
import { Info, PackagePlus, Pencil, X, Flag, Handshake } from 'lucide-react'
import { normalizePrintingLabel } from '../../../utils/variantLabeling'
import { useCurrency, type Currency } from '../../../contexts/CurrencyContext'
import styles from './Inventory.module.css'
import type { InventoryProduct } from './inventoryTypes'
import { inventoryCopy } from './inventoryCopy'
import { useInventoryRowDerived } from './useInventoryRowDerived'
import { formatStoreAmount } from './pricing'
import { formatSoldDate, heldForOrders, isSoldOut, showsSales, unitsSoldFor } from './inventorySalesView'
import { useOrgLocale } from '../../../hooks/useOrgLocale'

const CARD_IMG_WIDTH = 220
const CARD_IMG_HEIGHT = 308

export type InventoryProductCardProps = {
  product: InventoryProduct
  density?: 'comfortable' | 'compact'
  /** The Recent sales window, when the list has one. */
  soldWindowDays?: number | null
  alternate?: boolean
  selected: boolean
  canBulkOps: boolean
  canInventoryWrite: boolean
  removingPriceId: string | null
  onToggleSelect: () => void
  onImageClick: (url: string, alt: string) => void
  onEdit: () => void
  /** Mark some of this row's units as a consignor's. */
  onSetConsignment: () => void
  onPriceHistory: () => void
  onRemoveSale: () => void
  onRemoveManual: () => void
  onReport: (effectivePriceCents: number | null) => void
  onStarterLockedSale: () => void
  onStarterLockedManual: () => void
}

const c = inventoryCopy.card
const s = inventoryCopy.sales

export function InventoryProductCard({
  product,
  density = 'comfortable',
  soldWindowDays = null,
  alternate = false,
  selected,
  canBulkOps,
  canInventoryWrite,
  removingPriceId,
  onToggleSelect,
  onImageClick,
  onEdit,
  onSetConsignment,
  onPriceHistory,
  onRemoveSale,
  onRemoveManual,
  onReport,
  onStarterLockedSale,
  onStarterLockedManual,
}: InventoryProductCardProps) {
  const { formatPrice } = useCurrency()
  const d = useInventoryRowDerived(product)
  // Listed amounts are in the listing's own currency; with no server price there is nothing honest to show.
  const formatListed = (cents: number | null): string =>
    formatStoreAmount(formatPrice, cents, d.effectivePriceCurrency, c.priceUnavailable)
  // The other store figures (market, manual, floor, cost, differences): same engine, same rate.
  const formatStore = (cents: number | null): string =>
    formatStoreAmount(formatPrice, cents, d.storeCurrency, c.priceUnavailable)
  const totalValueTitle = d.listingMarkupPercent > 0 ? c.markupIncluded(d.listingMarkupPercent) : undefined
  const [primaryImageFailed, setPrimaryImageFailed] = useState(false)
  const locale = useOrgLocale()

  const sales = product.sales ?? null
  const salesRow = showsSales(product, soldWindowDays)
  const lastSoldLabel = sales ? formatSoldDate(sales.lastSoldAt, locale) : s.neverSold
  const lastSalePrice = sales ? formatPrice(sales.lastSale.unitCents, sales.lastSale.currency as Currency) : null
  const unitsSold = unitsSoldFor(sales, soldWindowDays)
  const unitsLabel = soldWindowDays !== null ? s.unitsInWindow(soldWindowDays) : s.unitsAllTime
  const held = heldForOrders(product)
  const heldBadge = held > 0 && <span className={styles.badgeHeld} title={s.heldTitle(held)}>{s.held} {held}</span>
  const editLabel = isSoldOut(product) ? s.restock : c.edit
  const EditIcon = isSoldOut(product) ? PackagePlus : Pencil

  useEffect(() => {
    setPrimaryImageFailed(false)
  }, [product.id, d.primaryImageUrl])

  if (import.meta.env.DEV) {
    const pq = Number(product.quantity ?? 0)
    const pa = product.availableQuantity
    const pr = product.reservedQuantity
    if (pq > 0 && typeof pa === 'number' && typeof pr !== 'number') {
      console.warn('[inventory] missing reservedQuantity while quantity > 0', {
        productId: product.id,
        quantity: pq,
        availableQuantity: pa,
      })
    }
    if (typeof pa === 'number' && typeof pr === 'number' && pq >= 0 && Math.abs(pa + pr - pq) > 0.0001) {
      console.warn('[inventory] quantity vs available + reserved mismatch', {
        productId: product.id,
        quantity: pq,
        availableQuantity: pa,
        reservedQuantity: pr,
      })
    }
  }

  // Three pre-release states share one badge slot: hidden until release, taking
  // pre-orders, or on sale early with no pre-order dressing (a permitted street-
  // date break). "EARLY" gets its own colour so it does not read as either.
  const preReleaseBadgeLabel =
    d.preReleaseMode === 'preorder' ? c.preorder : d.preReleaseMode === 'available' ? c.earlyRelease : c.draft
  const preReleaseBadgeClass =
    d.preReleaseMode === 'preorder'
      ? styles.badgePreorder
      : d.preReleaseMode === 'available'
        ? styles.badgeEarlyRelease
        : styles.badgeDraft
  const preReleaseBadgeTitle =
    d.preReleaseMode === 'available'
      ? `Not released yet, but listed as normal stock — customers see no pre-order label${d.preReleaseDateLabel ? ` (${c.releases.toLowerCase()} ${d.preReleaseDateLabel})` : ''}`
      : d.preReleaseDateLabel
        ? `${c.releases} ${d.preReleaseDateLabel}`
        : undefined

  const rowKeyHandler = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!canBulkOps) return
    // Keys on a control inside the row (the name, Edit, …) belong to that control: Enter on the
    // name opens the product, it must not also toggle the row's selection.
    if (e.target !== e.currentTarget) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onToggleSelect()
    }
  }

  // Each variant of a custom listing is its own row; these say which listing, and that a row is held.
  const listingBadges = (
    <>
      {product.pushHold && (
        <span className={styles.badgeDraft} title={product.pushHold.reason ?? 'Held from pushing until its listing is released'}>Held</span>
      )}
      {(product.listing?.optionNames.length ?? 0) > 0 && (
        <span className={styles.badgeChannels} title={`Variant of ${product.listing?.title ?? 'a listing'}`}>Variant</span>
      )}
    </>
  )

  // The name opens the product drawer; a click anywhere else on the row still toggles selection
  // (C1's batch flow), and the checkbox does too.
  const nameButton = (
    <button
      type="button"
      className={styles.productNameButton}
      onClick={(event) => {
        event.stopPropagation()
        onEdit()
      }}
      title={c.open}
    >
      {d.displayCardName}
    </button>
  )

  if (density === 'compact') {
    const compactMeta = [
      d.productSetName,
      d.productSetNumber ? `#${d.productSetNumber}` : null,
      product.variantData?.printing ? normalizePrintingLabel(product.variantData.printing) : null,
      product.variantData?.condition,
    ].filter(Boolean)

    return (
      <div
        className={`${styles.productCardCompact} ${salesRow ? styles.productCardCompactSales : ''} ${alternate ? styles.productCardCompactAlternate : ''} ${selected ? styles.selected : ''}`}
        {...(canBulkOps
          ? {
              role: 'button',
              tabIndex: 0,
              onClick: onToggleSelect,
              onKeyDown: rowKeyHandler,
              'aria-pressed': selected,
              'aria-label': `Toggle selection for ${d.displayCardName}`,
            }
          : {})}
      >
        <div className={styles.compactSelect} onClick={(event) => event.stopPropagation()}>
          {canBulkOps && (
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggleSelect}
              className={styles.selectCheckbox}
              aria-label={`Select ${d.displayCardName}`}
            />
          )}
        </div>

        <div className={styles.compactImage}>
          {d.primaryImageUrl && !primaryImageFailed ? (
            <img
              src={d.primaryImageUrl}
              alt=""
              width={56}
              height={78}
              loading="lazy"
              decoding="async"
              onError={() => setPrimaryImageFailed(true)}
              onClick={(event) => {
                event.stopPropagation()
                onImageClick(d.primaryImageUrl!, d.primaryImageAlt)
              }}
            />
          ) : (
            <div className={styles.compactNoImage} aria-hidden>🖼️</div>
          )}
        </div>

        <div className={styles.compactIdentity}>
          <div className={styles.compactTitleRow}>
            <h3>{nameButton}</h3>
            {heldBadge}
            {d.isPreRelease && (
              <span className={preReleaseBadgeClass}>{preReleaseBadgeLabel}</span>
            )}
            {d.isListingDrafted && <span className={styles.badgeDraft}>{c.hidden}</span>}
            {d.hasChannelOverride && <span className={styles.badgeChannels}>{c.channels}</span>}
            {listingBadges}
            {d.isCustom && (
              <span className={styles.badgeCustom} title={d.customCategoryLabel ?? undefined}>
                {d.eventRemovalLabel ? c.event : c.custom}
              </span>
            )}
            {d.manualActive && <span className={styles.badgeManual}>{c.manual}</span>}
            {(product.consignedQuantity ?? 0) > 0 && <span className={styles.badgeConsigned} title={c.consigned}>{c.consigned} {product.consignedQuantity}</span>}
            {d.showSaleIndicators && <span className={styles.badgeSale}>{d.saleActive ? c.onSale : c.sale}</span>}
          </div>
          {compactMeta.length > 0 && <div className={styles.compactMeta}>{compactMeta.join(' · ')}</div>}
          <div className={styles.compactSku}>{product.sku}</div>
        </div>

        <div className={styles.compactMetric}>
          <span>{c.available}</span>
          <strong>{product.availableQuantity}</strong>
          <small>{product.quantity} {c.total.replace(':', '').toLowerCase()}{(product.consignedQuantity ?? 0) > 0 ? ` · ${product.consignedQuantity} ${c.consigned.toLowerCase()}` : ''}</small>
        </div>

        <div className={styles.compactMetric}>
          <span>{d.saleActive ? c.saleLabel : c.listedPrice}</span>
          <strong>{formatListed(d.effectivePriceCents)}</strong>
          {d.saleActive && d.saleBasePriceCents !== null && (
            <small className={styles.compactOriginalPrice}>{formatListed(d.saleBasePriceCents)}</small>
          )}
        </div>

        {salesRow ? (
          <div className={`${styles.compactMetric} ${styles.compactSales}`}>
            <span>{soldWindowDays !== null ? unitsLabel : s.lastSold}</span>
            <strong>{soldWindowDays !== null ? unitsSold : lastSoldLabel}</strong>
            {sales && (
              <small>
                {soldWindowDays !== null ? lastSoldLabel : `${unitsSold} sold`} · {lastSalePrice}
              </small>
            )}
          </div>
        ) : (
          <div className={`${styles.compactMetric} ${styles.compactValue}`} title={totalValueTitle}>
            <span>{c.totalValue}</span>
            <strong>{formatListed(d.effectivePriceCents === null ? null : product.quantity * d.effectivePriceCents)}</strong>
          </div>
        )}

        <div className={styles.compactActions}>
          {product.cardId && (
            <button type="button" onClick={(event) => { event.stopPropagation(); onPriceHistory() }} title={c.pricingInsights} aria-label={c.pricingInsights}>
              <Info size={17} strokeWidth={2} aria-hidden />
            </button>
          )}
          <button type="button" onClick={(event) => { event.stopPropagation(); onEdit() }} title={editLabel} aria-label={editLabel} disabled={!canInventoryWrite}>
            <EditIcon size={17} strokeWidth={2} aria-hidden />
          </button>
          <button type="button" onClick={(event) => { event.stopPropagation(); onSetConsignment() }} title={c.setConsignment} aria-label={c.setConsignment} disabled={!canInventoryWrite}>
            <Handshake size={17} strokeWidth={2} aria-hidden />
          </button>
          {product.onSale && (
            <button type="button" onClick={(event) => { event.stopPropagation(); canInventoryWrite ? onRemoveSale() : onStarterLockedSale() }} title={c.removeSale} aria-label={c.removeSale} disabled={!canInventoryWrite}>
              <X size={17} strokeWidth={2} aria-hidden />
            </button>
          )}
          {d.hasManualPriceConfigured && (
            <button type="button" onClick={(event) => { event.stopPropagation(); if (canInventoryWrite && removingPriceId !== product.id) onRemoveManual(); else if (!canInventoryWrite) onStarterLockedManual() }} title={c.removeManual} aria-label={c.removeManual} disabled={!canInventoryWrite || removingPriceId === product.id}>
              <X size={17} strokeWidth={2} aria-hidden />
            </button>
          )}
          {product.cardId && (
            <button type="button" onClick={(event) => { event.stopPropagation(); onReport(d.effectivePriceCents) }} title={c.report} aria-label={c.report}>
              <Flag size={16} strokeWidth={2} aria-hidden />
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      className={`${styles.productCard} ${selected ? styles.selected : ''}`}
      {...(canBulkOps
        ? {
            role: 'button',
            tabIndex: 0,
            onClick: onToggleSelect,
            onKeyDown: rowKeyHandler,
            'aria-pressed': selected,
            'aria-label': `Toggle selection for ${d.displayCardName}`,
          }
        : {})}
    >
      {canBulkOps && (
        <div className={styles.productSelect} onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            className={styles.selectCheckbox}
            aria-label={`Select ${d.displayCardName}`}
          />
        </div>
      )}

      <div className={styles.productImage}>
        {d.primaryImageUrl && !primaryImageFailed ? (
          <picture className={styles.productPicture}>
            <img
              src={d.primaryImageUrl}
              alt={d.primaryImageAlt}
              width={CARD_IMG_WIDTH}
              height={CARD_IMG_HEIGHT}
              sizes="(max-width: 768px) min(50vw, 220px), clamp(160px, 14vw, 220px)"
              loading="lazy"
              decoding="async"
              onError={() => setPrimaryImageFailed(true)}
              onClick={(event) => {
                event.stopPropagation()
                onImageClick(d.primaryImageUrl!, d.primaryImageAlt)
              }}
              className={styles.productPictureImg}
            />
          </picture>
        ) : (
          <div className={styles.noImage} title="Image not available">
            {c.noImage}
          </div>
        )}
      </div>

      <div className={styles.productDetails}>
        <div className={styles.productHeader}>
          <h3>{nameButton}</h3>
          {heldBadge}
          {d.isPreRelease && (
            <span
              className={preReleaseBadgeClass}
              title={preReleaseBadgeTitle}
            >
              {preReleaseBadgeLabel}
              {d.preReleaseDateLabel && (
                <span className={styles.badgeMeta}> • {c.releases} {d.preReleaseDateLabel}</span>
              )}
            </span>
          )}
          {d.isListingDrafted && (
            <span className={styles.badgeDraft} title="Hidden from your storefront (listing set to draft)">
              {c.hidden}
            </span>
          )}
          {d.hasChannelOverride && (
            <span
              className={styles.badgeChannels}
              title={`Listed on ${d.channelOverrideCount} chosen sales channel${d.channelOverrideCount === 1 ? '' : 's'} instead of your store default`}
            >
              {c.channels}
            </span>
          )}
          {listingBadges}
          {d.isCustom && (
            <span
              className={styles.badgeCustom}
              title={
                d.eventRemovalLabel
                  ? `Event listing — removed automatically on ${d.eventRemovalLabel}`
                  : `Custom product${d.customCategoryLabel ? ` — ${d.customCategoryLabel}` : ''} (not from the card catalog)`
              }
            >
              {d.eventRemovalLabel ? c.event : c.custom}
              {d.eventRemovalLabel && (
                <span className={styles.badgeMeta}> • {c.removedAt} {d.eventRemovalLabel}</span>
              )}
            </span>
          )}
          {d.hasManualPriceConfigured && d.manualActive && (
            <span className={styles.badgeManual} title={product.manualPriceExpiresAt ?? undefined}>
              {c.manual}
              {d.manualPriceTimingLabel && <span className={styles.badgeMeta}> • {d.manualPriceTimingLabel}</span>}
            </span>
          )}

          {d.showSaleIndicators && (
            <span className={styles.badgeSale} title={product.saleEndAt ?? product.saleInfo?.saleEndAt ?? undefined}>
              {d.saleActive ? c.onSale : c.sale}
              {d.saleBadgeSuffix && <span className={styles.badgeMeta}>{d.saleBadgeSuffix}</span>}
              {d.saleTimingLabel && <span className={styles.badgeMeta}> • {d.saleTimingLabel}</span>}
            </span>
          )}
          {(product.consignedQuantity ?? 0) > 0 && (
            <span className={styles.badgeConsigned} title="Some of these units belong to consignors">
              {c.consigned} {product.consignedQuantity}
            </span>
          )}
        </div>

        {d.hasVariantChips && (
          <div className={styles.variantInfo}>
            {d.customCategoryLabel && (
              <span className={`${styles.variantBadge} ${styles.setCodeBadge}`}>
                <span className={styles.variantBadgeLabel}>Type:</span>
                <span className={styles.variantBadgeValue}>{d.customCategoryLabel}</span>
              </span>
            )}
            {d.productSetName && (
              <span className={`${styles.variantBadge} ${styles.setNameBadge}`}>
                <span className={styles.variantBadgeLabel}>{d.isCustom ? 'Collection:' : 'Set:'}</span>
                <span className={styles.variantBadgeValue}>{d.productSetName}</span>
              </span>
            )}
            {d.productSetCode && (
              <span className={`${styles.variantBadge} ${styles.setCodeBadge}`}>
                <span className={styles.variantBadgeLabel}>Code:</span>
                <span className={styles.variantBadgeValue}>{d.productSetCode}</span>
              </span>
            )}
            {d.productSetNumber && (
              <span className={`${styles.variantBadge} ${styles.setNumberBadge}`}>
                <span className={styles.variantBadgeLabel}>Set #:</span>
                <span className={styles.variantBadgeValue}>{d.productSetNumber}</span>
              </span>
            )}
            {d.showRarityBadge && (
              <span className={`${styles.variantBadge} ${styles.rarityBadge} ${d.rarityClassName}`}>
                <span className={styles.variantBadgeLabel}>Rarity:</span>
                <span className={styles.variantBadgeValue}>{d.productRarityLabel}</span>
              </span>
            )}
            {product.variantData?.printing && (
              <span className={`${styles.variantBadge} ${styles[d.printingClassKey]}`}>
                <span className={styles.variantBadgeLabel}>Printing:</span>
                <span className={styles.variantBadgeValue}>{normalizePrintingLabel(product.variantData.printing)}</span>
              </span>
            )}
            {product.variantData?.condition && (
              <span className={`${styles.variantBadge} ${styles[d.conditionClassKey]}`}>
                <span className={styles.variantBadgeLabel}>Condition:</span>
                <span className={styles.variantBadgeValue}>{product.variantData.condition}</span>
              </span>
            )}
            {d.productGameName && (
              <span className={`${styles.variantBadge} ${styles.gameBadge} ${d.gameAccentClassName}`}>
                <span className={styles.variantBadgeLabel}>{d.isCustom ? 'Brand:' : 'Game:'}</span>
                <span className={styles.variantBadgeValue}>{d.productGameName}</span>
              </span>
            )}
          </div>
        )}

        <div className={styles.productSku}>
          <span className={styles.skuLabel}>{c.sku}</span>
          <span className={styles.skuValue}>{product.sku}</span>
        </div>
      </div>

      <div className={styles.productInventory}>
        <div className={styles.inventorySection}>
          <div className={styles.inventoryLabel}>{c.quantity}</div>
          <div className={styles.inventoryValueGroup}>
            <div className={styles.inventoryItem}>
              <span className={styles.label}>{c.total}</span>
              <span className={styles.value}>{product.quantity}</span>
            </div>
            <div className={styles.inventoryItem}>
              <span className={styles.label}>{c.available}</span>
              <span className={`${styles.value} ${styles.available}`}>{product.availableQuantity}</span>
            </div>
            {(product.consignedQuantity ?? 0) > 0 && (
              <div className={styles.inventoryItem}>
                <span className={styles.label}>{c.consigned}</span>
                <span className={`${styles.value} ${styles.consignedValue}`}>{product.consignedQuantity}</span>
              </div>
            )}
            {product.reservedQuantity > 0 && (
              <div className={styles.inventoryItem}>
                <span className={styles.label}>{c.reserved}</span>
                <span className={`${styles.value} ${styles.reserved}`}>{product.reservedQuantity}</span>
              </div>
            )}
          </div>
        </div>

        <div className={styles.inventorySection}>
          <div className={styles.inventoryLabel}>{c.pricing}</div>
          <div className={styles.inventoryValueGroup}>
            <div className={styles.inventoryItem}>
              <span className={styles.label}>{c.marketPrice}</span>
              <div className={styles.priceWithChange}>
                <div className={styles.priceMainRow}>
                  <span className={`${styles.value} ${styles.price}`}>{formatStore(d.autoPriceCents)}</span>
                  {d.hasMarketComparison && (
                    <span
                      className={`${styles.priceChange} ${d.marketDiffCents! > 0 ? styles.priceUp : d.marketDiffCents! < 0 ? styles.priceDown : styles.priceFlat}`}
                      title={
                        d.marketDiffCents === 0
                          ? `No market movement in the last 24h${product.lastPriceUpdate ? ` (last change ${new Date(product.lastPriceUpdate).toLocaleDateString()})` : ''}`
                          : d.marketPreviousCents !== null
                            ? `Market moved ${formatStore(d.marketPreviousCents)} → ${formatStore(d.marketPriceCents)} in the last 24h${product.lastPriceUpdate ? ` (${new Date(product.lastPriceUpdate).toLocaleDateString()})` : ''}`
                            : 'Market movement over the last 24h'
                      }
                    >
                      24h {d.marketDiffCents! > 0 ? '▲' : d.marketDiffCents! < 0 ? '▼' : '•'} {formatStore(Math.abs(d.marketDiffCents!))}
                    </span>
                  )}
                </div>
                {d.isMinimumFloorApplied && d.minimumFloorCents !== null && (
                  <div className={styles.priceMetaRow}>
                    <span className={`${styles.priceMeta} ${styles.priceMetaLabel}`}>{c.floorActive}</span>
                    <span className={styles.priceMeta}>
                      {c.market} {formatStore(d.marketPriceCents)}
                    </span>
                    <span className={styles.priceMeta}>
                      {c.minimum} {formatStore(d.minimumFloorCents)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {product.costBasisCents !== null && (
              <div className={styles.inventoryItem}>
                <span
                  className={styles.label}
                  title={
                    d.costBasisUsdCents !== null && d.listingUsdToStoreRate !== null
                      ? c.costConverted((d.costBasisUsdCents / 100).toFixed(2), d.listingUsdToStoreRate)
                      : 'What you paid to acquire this product'
                  }
                >
                  {c.purchaseCost}
                </span>
                <div className={styles.priceWithChange}>
                  <div className={styles.priceMainRow}>
                    <span className={styles.value}>{d.costBasisCents !== null ? '≈ ' : ''}{formatStore(d.costBasisCents)}</span>
                    {d.profitCents !== null && (d.autoPriceCents ?? 0) > 0 && (d.costBasisCents ?? 0) > 0 && (
                      <span
                        className={`${styles.priceDiff} ${d.profitCents >= 0 ? styles.diffUp : styles.diffDown}`}
                        title={`${d.profitCents >= 0 ? 'Profit' : 'Loss'} vs market price`}
                      >
                        {d.profitCents >= 0 ? '+' : '-'}
                        {formatStore(Math.abs(d.profitCents))}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {d.manualActive && (
              <div className={styles.inventoryItem}>
                <span className={styles.label}>{c.manualPrice}</span>
                <div className={styles.priceWithChange}>
                  <div className={styles.priceMainRow}>
                    <span
                      className={`${styles.value} ${styles.price}`}
                      title={d.manualPriceExact ? 'Exactly the amount you typed' : undefined}
                    >
                      {formatStore(d.manualPriceCents)}
                    </span>
                    {d.manualDiffCents !== null && d.manualDiffCents !== 0 && (
                      <span
                        className={`${styles.priceDiff} ${d.manualDiffCents > 0 ? styles.diffUp : styles.diffDown}`}
                        title="Difference vs market price"
                      >
                        {d.manualDiffCents > 0 ? '+' : '-'}
                        {formatStore(Math.abs(d.manualDiffCents))}
                      </span>
                    )}
                  </div>
                  {d.manualPriceTimingLabel && (
                    <div className={styles.priceMetaRow}>
                      <span className={styles.priceMeta} title={product.manualPriceExpiresAt ?? undefined}>
                        {d.manualPriceTimingLabel}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {d.showSaleIndicators && d.saleLabel && (
              <div className={styles.inventoryItem}>
                <span className={styles.label}>{c.saleLabel}</span>
                <div className={styles.priceWithChange}>
                  <div className={styles.priceMainRow}>
                    {d.saleActive && d.saleBasePriceCents !== null && d.salePriceCents !== null ? (
                      <>
                        <span className={styles.originalPrice}>{formatListed(d.saleBasePriceCents)}</span>
                        <span className={`${styles.value} ${styles.price}`}>{formatListed(d.salePriceCents)}</span>
                        {d.saleDiscountCents !== null && d.saleDiscountCents > 0 && (
                          <span className={`${styles.priceDiff} ${styles.diffDown}`} title="Discount vs base price">
                            -{formatListed(d.saleDiscountCents)}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className={`${styles.value} ${styles.price}`}>{d.saleLabel}</span>
                    )}
                  </div>
                  {(d.saleMetaPrimary || d.saleActive) && (
                    <div className={styles.priceMetaRow}>
                      {d.saleMetaPrimary && <span className={styles.priceMeta}>{d.saleMetaPrimary}</span>}
                      {d.saleActive && (
                        <span className={styles.priceMeta}>
                          {d.manualActive ? c.basedOnManual : c.basedOnMarket}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {salesRow ? (
          <div className={styles.productValue}>
            <div className={styles.totalValueLabel}>{s.lastSold}</div>
            <div className={styles.lastSoldValue}>{lastSoldLabel}</div>
            <dl className={styles.salesFigures}>
              {sales && <div><dt>{unitsLabel}</dt><dd>{unitsSold}</dd></div>}
              {sales && <div><dt>{s.lastPrice}</dt><dd>{lastSalePrice}</dd></div>}
              <div><dt>{s.listedNow}</dt><dd>{formatListed(d.effectivePriceCents)}</dd></div>
            </dl>
          </div>
        ) : (
          <div className={styles.productValue} title={totalValueTitle}>
            <div className={styles.totalValueLabel}>{c.totalValue}</div>
            <div className={styles.totalValueAmount}>{formatListed(d.effectivePriceCents === null ? null : product.quantity * d.effectivePriceCents)}</div>
          </div>
        )}
      </div>

      <div className={styles.productActions}>
        {product.cardId && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onPriceHistory()
            }}
            className={styles.btnInfo}
            title={c.pricingInsights}
            aria-label={c.pricingInsights}
            type="button"
          >
            <Info size={20} strokeWidth={2} aria-hidden />
            {c.pricingInsights}
          </button>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation()
            onEdit()
          }}
          className={styles.btnEdit}
          title={isSoldOut(product) ? s.restockTitle : 'Stock, pricing, listing, details and history'}
          aria-label={editLabel}
          disabled={!canInventoryWrite}
          type="button"
        >
          <EditIcon size={20} strokeWidth={2} aria-hidden />
          {editLabel}
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation()
            onSetConsignment()
          }}
          className={styles.btnEdit}
          title="Mark units of this product as a consignor's"
          aria-label={c.setConsignment}
          disabled={!canInventoryWrite}
          type="button"
        >
          <Handshake size={20} strokeWidth={2} aria-hidden />
          {c.setConsignment}
        </button>

        {product.onSale && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              if (!canInventoryWrite) {
                onStarterLockedSale()
                return
              }
              onRemoveSale()
            }}
            className={styles.btnRemoveSale}
            title={c.removeSale}
            aria-label={c.removeSale}
            disabled={!canInventoryWrite}
            type="button"
          >
            <X size={16} strokeWidth={2} aria-hidden />
            {c.removeSale}
          </button>
        )}

        {d.hasManualPriceConfigured && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              if (!canInventoryWrite) {
                onStarterLockedManual()
                return
              }
              if (removingPriceId === product.id) return
              onRemoveManual()
            }}
            className={styles.btnRemoveManual}
            title={c.removeManual}
            aria-label={c.removeManual}
            disabled={!canInventoryWrite || removingPriceId === product.id}
            type="button"
          >
            <X size={16} strokeWidth={2} aria-hidden />
            {c.removeManual}
          </button>
        )}

        {product.cardId && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onReport(d.effectivePriceCents)
            }}
            className={styles.btnReport}
            title="Report an issue with this card"
            type="button"
          >
            <Flag size={14} strokeWidth={2} aria-hidden />
            {c.report}
          </button>
        )}
      </div>
    </div>
  )
}
